import { isEmpty } from 'lodash-es';
import { Area, IViolation } from 'src/app/models';
import { TaskListCharacteristic } from 'src/app/models/domain';

/** The TS operation whose characteristics carry the M-codes compared against the 2D. */
const dimensionOperation = 'Dimension';

/**
 * Counts describing why the rule could not run, for the "Report this" action. Deliberately
 * only shapes and counts — never M-code values or drawing content — so the report can be sent
 * for an unreleased element by a user whose colleagues cannot be shown its data.
 */
export interface IMCodeValidationDiagnostics {
  /** Which branch fired, as a fixed value rather than free-form prose. */
  reason:
    | 'no-baseline-revision'
    | 'no-mcode-data'
    | 'no-mcodes-on-2d'
    | 'no-characteristics'
    | 'no-dimension-characteristics';
  /** e.g. `PD10008469/A`, absent when no baseline revision was found. */
  revision?: string;
  /** undefined when the M-code lookup returned no data envelope at all. */
  mCodeCount?: number;
  characteristicCount: number;
  dimensionCharacteristicCount: number;
}

/** Raised when the rule has no data to compare, as opposed to finding a genuine mismatch. */
export class MCodeValidationNotPossibleError extends Error {
  public constructor(
    message: string,
    public readonly diagnostics: IMCodeValidationDiagnostics
  ) {
    super(message);
  }
}

/**
 * Validates that all M-codes in the 2D drawing in Teamcenter are also present in the Technical Specification in MCS.
 *
 * @param characteristics all master characteristics for the equipment, unfiltered — the
 * `Dimension` filter happens here so the "nothing to compare" message can distinguish an
 * empty TS from a TS with no `Dimension` operation.
 * @throws MCodeValidationNotPossibleError when either side is empty — see the class doc.
 */
export abstract class Teamcenter2DMCodeValidation {
  public static validate(
    characteristics: TaskListCharacteristic[],
    teamcenter2DMCodes: string[] | undefined,
    revisionDescription?: string
  ): IViolation[] {
    const revisionSuffix = revisionDescription ? ` (checked ${revisionDescription})` : '';

    const dimensionCharacteristics = characteristics.filter(
      (characteristic) => characteristic.infoField2 === dimensionOperation
    );

    const counts = {
      characteristicCount: characteristics.length,
      dimensionCharacteristicCount: dimensionCharacteristics.length,
      mCodeCount: teamcenter2DMCodes?.length,
      revision: revisionDescription,
    };

    // Two different causes both arrive as undefined: no baseline revision to ask about, or a
    // baseline was asked about and the M-code lookup came back with no data envelope.
    // revisionDescription is only set once a revision has been chosen, so it separates them.
    if (teamcenter2DMCodes === undefined) {
      throw new MCodeValidationNotPossibleError(
        revisionDescription
          ? `The M-code lookup for ${revisionDescription} returned no data, so the 2D M-codes could not be validated. If this is an error, then please use the report button below.`
          : 'No baseline revision of the part design was found in Teamcenter, so the 2D M-codes could not be validated.',
        { ...counts, reason: revisionDescription ? 'no-mcode-data' : 'no-baseline-revision' }
      );
    }

    // Neither side being empty is a mismatch — with nothing to compare, the comparison is
    // vacuous. Reporting "every code is missing" reads as a real finding and hides the fact
    // that one side was never read.
    if (isEmpty(teamcenter2DMCodes)) {
      throw new MCodeValidationNotPossibleError(
        `No M-codes found on the 2D of the part design baseline in Teamcenter${revisionSuffix}, so there was nothing to validate the TS against. If this is an error, then please use the report button below.`,
        { ...counts, reason: 'no-mcodes-on-2d' }
      );
    }

    if (isEmpty(dimensionCharacteristics)) {
      throw new MCodeValidationNotPossibleError(
        isEmpty(characteristics)
          ? `The TS returned no characteristics, so the ${teamcenter2DMCodes.length} M-code(s) on the 2D${revisionSuffix} could not be validated against it. The TS may not exist for this equipment, or may have no Master specification.`
          : `The TS has ${characteristics.length} characteristic(s) but none in the '${dimensionOperation}' operation, so the ${teamcenter2DMCodes.length} M-code(s) on the 2D${revisionSuffix} could not be validated against it.`,
        {
          ...counts,
          reason: isEmpty(characteristics) ? 'no-characteristics' : 'no-dimension-characteristics',
        }
      );
    }

    const missingMCodes = Teamcenter2DMCodeValidation.getMissingMCodes(
      dimensionCharacteristics,
      teamcenter2DMCodes
    );

    if (isEmpty(missingMCodes)) {
      return [];
    }

    // for better pathToViolation
    const characteristic = dimensionCharacteristics[0];

    return [
      {
        message: `
              Some M-codes from the 2D are missing in the TS.
              Missing ${missingMCodes.length} of ${teamcenter2DMCodes.length} code(s) on the 2D${revisionSuffix}: ${missingMCodes.join(' ')}
            `,
        pathToViolation: [
          {
            area: Area.Specifications,
          },
          {
            area: Area.TechnicalSpecification,
            displayId: `${characteristic.taskListGroup}-${characteristic.groupCounter}`,
            id: `Q-${characteristic.taskListGroup}-${characteristic.groupCounter}`,
          },
          {
            area: Area.TechnicalSpecificationOperation,
            id: characteristic.infoField2,
          },
        ],
      },
    ];
  }

  private static getMCodeIdentifier(mCode: string): string | undefined {
    /**
     * Example 1: W3A: +/-1% Weight dev. calculated in %
     * Example 2: M4_W:Knob - Centre distance Width
     *
     * Group "identifier": W3A - M4_W
     */

    // Anchored, and on the trimmed text: unanchored, a leading space became part of the
    // identifier (" M4_L" never matches "M4_L") and a code with no leading letters swallowed
    // the whole line. Both sides go through here, so a mismatch silently reported every code.
    const match = /^(?<identifier>\D*\d+_?[A-Z]?)(?=[\s:]|$)/u.exec(mCode.trim());

    return match?.groups?.identifier;
  }

  private static getMissingMCodes(
    characteristics: TaskListCharacteristic[],
    teamcenter2DMCodes: string[]
  ) {
    const tsMCodes = new Set(
      characteristics.map((characteristic) =>
        Teamcenter2DMCodeValidation.getMCodeIdentifier(
          characteristic.shortTextInspectionCharacteristic
        )
      )
    );

    // An unparseable 2D code falls back to its raw text: it used to compare as `undefined`
    // against a TS side that could hold `undefined` too, so it either vanished or matched an
    // unrelated code.
    return teamcenter2DMCodes
      .map((tcMCode) => ({
        identifier: Teamcenter2DMCodeValidation.getMCodeIdentifier(tcMCode),
        raw: tcMCode.trim(),
      }))
      .filter(({ identifier }) => identifier === undefined || !tsMCodes.has(identifier))
      .map(({ identifier, raw }) => identifier ?? raw);
  }
}
